import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AnyAction } from 'redux'
import { ActionType } from 'typesafe-actions'
import { TApiError } from '../apiState/types'
import { NAMESPACE } from './constants'
import {
  TAdminAgencyMember,
  TAdminEmailMatch,
  TAdminGrantAccessRequest,
  TAdminMembershipRequest,
  TAdminUserLookup,
  TAgencyLoginRequest,
  TAgencyUser,
  TBulkAddOutcome,
  TAuthTokens,
  TChangePermissionsRequest,
  TCreateAgencyRequest,
  TUserEnabledChanged,
  TDecodedTokenEvent,
  TDisableUserRequest,
  TEnableUserRequest,
  TLoginRequest,
  TLogoutRequest,
  TOnboardingProgress,
  TResetPasswordLinkRequest,
  TResetPasswordRequest,
  TRevokeAccessRequest,
  TSegmentMembershipRequest,
  TSignupRequest,
  TTeamMember,
  TSwitchAgencyRequest,
  TUserDetails,
  TUserDetailsEntity,
  TUserDetailsRequest,
  TUserMisc,
  TUserState,
  TValidateOtpRequest,
  TVerifyEmailRequest,
} from './types'

const sliceName = `${NAMESPACE}Api`

const initialState: TUserState = {
  isAuthenticated: false,
  isRefreshingToken: false,
  authToken: null,
  refreshToken: null,
  hasSessionTimedOut: false,
  userId: null,
  email: '',
  emailVerified: false,
  cellVerified: false,
  firstName: '',
  lastName: '',
  cellNumber: '',
  idNumber: '',
  passport: '',
  dateOfBirth: '',
  currentAgencyId: null,
  currentAgencyName: '',
  currentAgencyRole: null,
  users: [],
  misc: {},
  onboardingProgress: {},
  otpErrors: [],
  isValidSupportStaff: false,
  team: [],
  currentSegments: [],
  segmentMemberships: [],
  userEnabled: false,
  userDisabled: false,
  agencies: [],
  adminAgencyMembers: {},
  adminUserLookup: null,
  adminGrantResults: null,
  adminEmailMatches: [],
}

/**
 * `http_error` as minted by `restful.restfulErrorEvent`, narrowed to the one
 * case this slice cares about: a failed OTP validation.
 */
type THttpErrorAction = AnyAction & {
  payload?: { response?: TApiError[] }
  meta?: { initAction?: string }
}

const isOtpValidationError = (action: AnyAction): action is THttpErrorAction =>
  action.type === 'http_error' && action.meta?.initAction === `${sliceName}/validateOtp_request`

/** The account shown on the agency-access page, if it is the one `email` names. */
const lookedUp = (state: TUserState, email: string) =>
  state.adminUserLookup?.email.toLowerCase() === email.trim().toLowerCase() ? state.adminUserLookup : null

const userSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    userResumed: (state, { payload }: PayloadAction<TAuthTokens>) => {},
    refreshToken_request: (state, { payload }: PayloadAction<unknown>) => {},
    refreshToken_success: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.authToken = payload.authToken
      state.refreshToken = payload.refreshToken
    },
    login_request: (state, { payload }: PayloadAction<TLoginRequest>) => {},
    login_success: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.isAuthenticated = true
      state.authToken = payload.authToken
      state.refreshToken = payload.refreshToken
      state.hasSessionTimedOut = false
      state.misc = payload.miscellaneous ?? {}
    },
    logout_request: () => {},
    /** Keeps the session alive for Eventy's who-is-online view; see useSessionHeartbeat. */
    heartbeat_request: () => {},
    logout_success: (state, { payload }: PayloadAction<TLogoutRequest>) => {
      state.userId = initialState.userId
      state.firstName = initialState.firstName
      state.lastName = initialState.lastName
      state.cellNumber = initialState.cellNumber
      state.currentAgencyId = initialState.currentAgencyId
      state.email = initialState.email
      state.isAuthenticated = initialState.isAuthenticated
      state.hasSessionTimedOut = payload?.timeOut ?? false
    },
    agencyLogin_request: (state, { payload }: PayloadAction<TAgencyLoginRequest>) => {},
    agencyLogin_success: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.isAuthenticated = true
      state.authToken = payload.authToken
      state.refreshToken = payload.refreshToken
      state.hasSessionTimedOut = false
    },
    created: (state, { payload }: PayloadAction<unknown>) => {},
    updated: (state, { payload }: PayloadAction<unknown>) => {},
    resetPasswordLink_request: (state, { payload }: PayloadAction<TResetPasswordLinkRequest>) => {},
    resetPasswordLink_success: (state, { payload }: PayloadAction<{ userId: string }>) => {},
    passwordReset_request: (state, { payload }: PayloadAction<TResetPasswordRequest>) => {},
    passwordReset_success: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.isAuthenticated = true
      state.authToken = payload.authToken
      state.refreshToken = payload.refreshToken
      state.hasSessionTimedOut = false
      state.misc = payload.miscellaneous ?? {}
    },
    agencySwitched: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.isAuthenticated = true
      state.authToken = payload.authToken
      state.refreshToken = payload.refreshToken
      state.hasSessionTimedOut = false
    },
    saveDetails_request: (state, { payload }: PayloadAction<TUserDetails>) => {},
    /**
     * The endpoint echoes the saved profile back, so the whole payload is
     * merged in. `TUserDetails` bounds what that spread can touch.
     */
    saveDetails_success: (state, { payload }: PayloadAction<TUserDetails>) => ({ ...state, ...payload }),
    currentUserDetails_request: () => {},
    currentUserDetails_success: (state, { payload }: PayloadAction<TUserDetails>) => {
      state.firstName = payload.firstName ?? ''
      state.lastName = payload.lastName ?? ''
      state.idNumber = payload.idNumber ?? ''
      state.passport = payload.passport ?? ''
      state.dateOfBirth = payload.dateOfBirth ?? ''
      state.cellNumber = payload.cellNumber ?? ''
      state.cellVerified = payload.cellVerified ?? false
      state.emailVerified = payload.emailVerified ?? false
    },
    userDetails_request: (state, { payload }: PayloadAction<TUserDetailsRequest>) => {},
    userDetails_success: (state, { payload }: PayloadAction<TUserDetailsEntity>) => {
      state.users.push(payload)
    },
    validateOtp_request: (state, { payload }: PayloadAction<TValidateOtpRequest>) => {},
    validateOtp_success: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.cellVerified = true
    },
    signup_request: (state, { payload }: PayloadAction<TSignupRequest>) => {},
    signupFromInvite_request: (state, { payload }: PayloadAction<TSignupRequest>) => {},
    signup_success: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.isAuthenticated = true
      state.authToken = payload.authToken
      state.refreshToken = payload.refreshToken
      state.hasSessionTimedOut = false
    },
    verifyEmail_request: (state, { payload }: PayloadAction<TVerifyEmailRequest>) => {},
    verifyEmail_success: () => {},
    resendEmailVerification_request: (state, { payload }: PayloadAction<unknown>) => {},
    resendEmailVerification_success: (state, { payload }: PayloadAction<null>) => {},
    createAgency_request: (state, { payload }: PayloadAction<TCreateAgencyRequest>) => {},
    createAgency_success: (state, { payload }: PayloadAction<TAuthTokens>) => {},
    userAuthTokenDecoded: (state, { payload }: PayloadAction<TDecodedTokenEvent>) => {
      const { value } = payload
      const membership = value?.agencyMembership
      state.userId = value?.userId ?? null
      state.firstName = value?.firstName ?? ''
      state.currentAgencyId = membership?.agencyId ?? null
      state.currentAgencyName = membership?.agencyName ?? ''
      state.currentAgencyRole = membership?.role ?? null
      state.email = value?.email ?? ''
      state.isValidSupportStaff = value?.validSupportStaff ?? false
      state.currentSegments = membership?.segment?.currentSegments ?? []
      state.segmentMemberships = membership?.segment?.segmentMemberships ?? []
    },
    userAuthTokenExpired: (state, { payload }: PayloadAction<TDecodedTokenEvent>) => {},
    updateMisc_request: (state, { payload }: PayloadAction<TUserMisc>) => {},
    updateMisc_success: (state, { payload }: PayloadAction<TUserMisc>) => {
      state.misc = payload
    },
    onboardingProgress_request: () => {},
    onboardingProgress_success: (state, { payload }: PayloadAction<TOnboardingProgress>) => {
      state.onboardingProgress = payload
    },
    team_request: () => {},
    team_success: (state, { payload }: PayloadAction<TTeamMember[]>) => {
      state.team = payload
    },
    revokeAccess_request: (state, { payload }: PayloadAction<TRevokeAccessRequest>) => {},
    revokeAccess_success: {
      reducer: (state, { meta }: PayloadAction<unknown, string, TRevokeAccessRequest>) => {
        state.team = state.team.filter(tm => tm.userId !== meta.userId)
      },
      prepare: (payload: unknown, meta: TRevokeAccessRequest) => ({ payload, meta, error: null }),
    },
    addSegmentMembership_request: (state, { payload }: PayloadAction<TSegmentMembershipRequest>) => {},
    addSegmentMembership_success: (state, { payload }: PayloadAction<TSegmentMembershipRequest>) => {
      // there's a slight delay between creating a segment and the segmentId being added to team member on the backend.
      // add to state via params passed through a successful request
      state.team = state.team.map(member =>
        member.userId === payload.userId
          ? {
              ...member,
              segments: [...(member.segments ?? []), payload.segmentId],
            }
          : member,
      )
    },
    deleteSegmentMembership_request: (state, { payload }: PayloadAction<TSegmentMembershipRequest>) => {},
    deleteSegmentMembership_success: (state, { payload }: PayloadAction<TSegmentMembershipRequest>) => {
      // mirrors addSegmentMembership_success: update from the request params rather than wait for a refetch
      state.team = state.team.map(member =>
        member.userId === payload.userId
          ? {
              ...member,
              segments: (member.segments ?? []).filter(segmentId => segmentId !== payload.segmentId),
            }
          : member,
      )
    },
    changePermissions_request: (state, { payload }: PayloadAction<TChangePermissionsRequest>) => {},
    changePermissions_success: (state, { payload }: PayloadAction<TChangePermissionsRequest>) => {
      state.team = state.team.map(member =>
        member.userId === payload.params.userId
          ? {
              ...member,
              role: payload.body.role,
            }
          : member,
      )
    },
    enableUser_request: (state, { payload }: PayloadAction<TEnableUserRequest>) => {},
    enableUser_success: (state, { payload }: PayloadAction<TUserEnabledChanged>) => {
      state.userEnabled = true
      const shown = lookedUp(state, payload.email)
      if (shown) shown.enabled = true
    },
    disableUser_request: (state, { payload }: PayloadAction<TDisableUserRequest>) => {},
    disableUser_success: (state, { payload }: PayloadAction<TUserEnabledChanged>) => {
      state.userDisabled = true
      const shown = lookedUp(state, payload.email)
      if (shown) shown.enabled = false
    },
    getAgencies_request: () => {},
    getAgencies_success: (state, { payload }: PayloadAction<TAgencyUser[]>) => {
      state.agencies = payload
    },
    adminAgencyMembers_request: (state, { payload }: PayloadAction<{ agencyId: string }>) => {},
    adminAgencyMembers_success: {
      reducer: (state, { payload, meta }: PayloadAction<TAdminAgencyMember[], string, { agencyId: string }>) => {
        state.adminAgencyMembers[meta.agencyId] = payload
      },
      prepare: (payload: TAdminAgencyMember[], meta: { agencyId: string }) => ({ payload, meta, error: null }),
    },
    adminLookupUser_request: (state, { payload }: PayloadAction<{ email: string }>) => {
      // The post-grant refresh re-reads the same email; keep the card and the grant outcomes
      // on screen for that. A different email starts clean.
      if (lookedUp(state, payload.email)) return
      state.adminUserLookup = null
      state.adminGrantResults = null
    },
    adminLookupUser_success: (state, { payload }: PayloadAction<TAdminUserLookup>) => {
      state.adminUserLookup = payload
    },
    adminSearchEmails_request: (state, { payload }: PayloadAction<{ q: string }>) => {},
    adminSearchEmails_success: (state, { payload }: PayloadAction<TAdminEmailMatch[]>) => {
      state.adminEmailMatches = payload
    },
    adminSearchEmails_clear: state => {
      state.adminEmailMatches = []
    },
    adminLookupUser_clear: state => {
      state.adminUserLookup = null
      state.adminGrantResults = null
    },
    adminGrantAccess_request: (state, { payload }: PayloadAction<TAdminGrantAccessRequest>) => {},
    adminGrantAccess_success: {
      reducer: (state, { payload }: PayloadAction<TBulkAddOutcome[], string, TAdminGrantAccessRequest>) => {
        state.adminGrantResults = payload
      },
      prepare: (payload: TBulkAddOutcome[], meta: TAdminGrantAccessRequest) => ({ payload, meta, error: null }),
    },
    adminRemoveMembership_request: (
      state,
      { payload }: PayloadAction<TAdminMembershipRequest & { email?: string }>,
    ) => {},
    adminRemoveMembership_success: {
      // Optimistic: the read side drops the row asynchronously, so a refetch straight away can
      // still return it.
      reducer: (state, { meta }: PayloadAction<unknown, string, TAdminMembershipRequest & { email?: string }>) => {
        const members = state.adminAgencyMembers[meta.agencyId]
        if (members) state.adminAgencyMembers[meta.agencyId] = members.filter(m => m.userId !== meta.userId)
        if (state.adminUserLookup?.userId === meta.userId) {
          state.adminUserLookup.agencies = state.adminUserLookup.agencies.filter(a => a.agencyId !== meta.agencyId)
        }
      },
      prepare: (payload: unknown, meta: TAdminMembershipRequest & { email?: string }) => ({
        payload,
        meta,
        error: null,
      }),
    },
    switchAgency_request: (state, { payload }: PayloadAction<TSwitchAgencyRequest>) => {},
    switchAgency_success: (state, { payload }: PayloadAction<TAuthTokens>) => {
      state.isAuthenticated = true
      state.authToken = payload.authToken
      state.refreshToken = payload.refreshToken
      state.hasSessionTimedOut = false
    },
  },
  extraReducers: builder => {
    /**
     * `addMatcher` rather than `addCase('http_error', ...)`: the global error
     * action is not part of this slice's union, so a case handler cannot carry
     * a payload or meta type.
     */
    builder.addMatcher(isOtpValidationError, (state, action) => {
      state.otpErrors = action.payload?.response ?? []
    })
  },
})

const { reducer, actions: events } = userSlice

export type UserEvent = ActionType<typeof events>

export { events, initialState, reducer }
